import React, { useState } from 'react';
import { supabase } from '../supabase';
import { toastError, toastSuccess } from '../ui/toast';
// ROLES import removed (unused in this component)
import { logAuditAction, AUDIT_ACTIONS, RESOURCE_TYPES } from '../utils/auditLog';
import { colors as theme } from '../theme';

/**
 * Modal for creating new coach/admin users
 * Accessible by super_admins and admins
 */
const CreateUserModal = ({ onClose, onUserCreated, coaches, currentUserRole, currentUserId }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'coach',
    coach_id: null
  });
  const [loading, setLoading] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // { email, password, name, role, coach_code } | null

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!formData.email || !formData.password || !formData.name) {
        toastError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      if (formData.password.length < 6) {
        toastError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      // Step 1: Check if user already exists in users table
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('email, role, status')
        .eq('email', formData.email)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing user:', checkError);
      }

      if (existingUser) {
        toastError(`Email already exists as a ${existingUser.role}. Use People to change their role instead.`);
        setLoading(false);
        return;
      }

      // Step 2: Save current session before creating new user
      const { data: { session: currentSession } } = await supabase.auth.getSession();

      // Step 3: Create auth user in Supabase Auth
      // Note: signUp will temporarily log us into the new user's account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name
          },
          emailRedirectTo: undefined // Don't send confirmation email for admin-created users
        }
      });

      if (authError) {
        // Handle specific error for already registered email
        if (authError.message?.includes('already registered') || authError.message?.includes('already been registered')) {
          toastError('This email is already registered. If they need a role change, use People to update their existing account.');
          setLoading(false);
          return;
        }
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create auth user');
      }

      // Step 4: Restore original session (log back in as admin/super admin)
      if (currentSession) {
        await supabase.auth.setSession({
          access_token: currentSession.access_token,
          refresh_token: currentSession.refresh_token
        });
      }

      // Step 6: Prepare user record data
      const userRecordData = {
        auth_user_id: authData.user.id,
        email: formData.email,
        name: formData.name,
        role: formData.role,
        status: 'active', // Pre-approved
        approved_at: new Date().toISOString(),
        approved_by: currentUserId || null,
        coach_id: formData.coach_id || null
      };

      // ✅ If admin creates a coach, auto-assign admin_id
      if (currentUserRole === 'admin' && formData.role === 'coach') {
        userRecordData.admin_id = currentUserId;
      }

      // Step 7: Create user record in users table
      const { data: newUser, error: userError } = await supabase
        .from('users')
        .insert(userRecordData)
        .select('coach_code')
        .single();

      if (userError) {
        // Auth user was created but users table insert failed.
        // The orphaned auth user (ID: authData.user.id) must be removed manually
        // from the Supabase Auth dashboard, or via a DB trigger on auth.users.
        console.error(
          'Failed to create user record. Orphaned auth user ID:',
          authData.user.id,
          'Error:',
          userError
        );
        throw new Error(
          `User profile could not be saved (${userError.message}). ` +
          `An orphaned auth account was created — please delete auth user ID ${authData.user.id} from Supabase Auth.`
        );
      }

      // Log the creation
      await logAuditAction(
        AUDIT_ACTIONS.CREATE_USER,
        RESOURCE_TYPES.USER,
        null,
        null,
        {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          status: 'active'
        }
      );

      // Success message based on role
      let successMessage = `${formData.role} account created for ${formData.name}`;
      if (currentUserRole === 'admin' && formData.role === 'coach') {
        successMessage += ' and assigned to you';
      }

      toastSuccess(successMessage);
      onUserCreated();

      // Show handoff screen instead of closing immediately — the super
      // admin needs to see the generated coach code to share it, and
      // Supabase never returns the plaintext password again after this.
      setCreatedInfo({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        coach_code: newUser?.coach_code || null,
      });
    } catch (err) {
      console.error('Error creating user:', err);

      // User-friendly error messages
      if (err.message?.includes('already registered')) {
        toastError('This email is already registered. Use People to update their role instead.');
      } else if (err.message?.includes('Invalid email')) {
        toastError('Please enter a valid email address');
      } else if (err.message?.includes('Password')) {
        toastError('Password must be at least 6 characters');
      } else {
        toastError(err.message || 'Failed to create user. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: theme.surface2,
        borderRadius: '14px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
      {createdInfo ? (
        <>
          <h2 style={{ color: theme.success, fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
            {createdInfo.role === 'coach' ? 'Coach account created' : 'Account created'}
          </h2>
          <p style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '20px' }}>
            Share these details with {createdInfo.name} so they can log in
            {createdInfo.coach_code ? ' and start giving out their code.' : '.'} This password won't be shown again.
          </p>

          {[
            { label: 'Email', value: createdInfo.email },
            { label: 'Password', value: createdInfo.password },
            ...(createdInfo.coach_code ? [{ label: 'Coach Code', value: createdInfo.coach_code }] : []),
          ].map(({ label, value }) => (
            <div key={label} style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: theme.text, fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                {label}
              </label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                backgroundColor: theme.surface1, border: `2px solid ${theme.surface3}`, borderRadius: '8px', padding: '12px',
              }}>
                <span style={{ flex: 1, color: theme.gold, fontSize: '14px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {value}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(value);
                    toastSuccess(`${label} copied`);
                  }}
                  style={{
                    backgroundColor: 'transparent', border: `1px solid ${theme.gold}`, color: theme.gold,
                    padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', fontWeight: 700,
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%', marginTop: '12px', padding: '12px 24px',
              backgroundColor: theme.gold, border: 'none', color: theme.surface1,
              borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700,
            }}
          >
            Done
          </button>
        </>
      ) : (
      <>
        {/* Header */}
        <h2 style={{
          color: theme.gold,
          fontSize: '24px',
          fontWeight: 700,
          marginBottom: '8px'
        }}>
          {currentUserRole === 'admin' ? 'Create New Coach' : 'Create New User'}
        </h2>
        <p style={{ color: theme.textMuted, fontSize: '14px', marginBottom: '12px' }}>
          {currentUserRole === 'admin'
            ? 'Create a new coach account. The coach will be automatically assigned to you and will be able to manage their own clients.'
            : 'Create a new coach or admin account. The account will be pre-approved and active.'}
        </p>
        <div style={{
          backgroundColor: theme.surface2,
          border: `1px solid ${theme.gold}`,
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '24px'
        }}>
          <p style={{ color: theme.gold, fontSize: '12px', margin: 0 }}>
            💡 <strong>Note:</strong> {currentUserRole === 'admin'
              ? 'A unique coach code (e.g., C-JOHN) will be automatically generated. Clients can use this code during signup to be assigned to your coach.'
              : 'If the email already exists, use "People" to change their role instead of creating a new account.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: theme.text,
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '8px'
            }}>
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: theme.surface1,
                border: `2px solid ${theme.surface3}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: theme.text,
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '8px'
            }}>
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: theme.surface1,
                border: `2px solid ${theme.surface3}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: theme.text,
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '8px'
            }}>
              Password *
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Minimum 6 characters"
              required
              minLength={6}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: theme.surface1,
                border: `2px solid ${theme.surface3}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px'
              }}
            />
            <p style={{ color: theme.textMuted, fontSize: '12px', marginTop: '4px' }}>
              User can change this password after first login
            </p>
          </div>

          {/* Role */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              color: theme.text,
              fontSize: '14px',
              fontWeight: 700,
              marginBottom: '8px'
            }}>
              Role *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: theme.surface1,
                border: `2px solid ${theme.surface3}`,
                borderRadius: '8px',
                color: theme.text,
                fontSize: '14px',
                cursor: 'pointer',
                opacity: 1
              }}
            >
              <option value="coach">Coach</option>
            </select>
            <p style={{ color: theme.textMuted, fontSize: '12px', marginTop: '4px' }}>
              {formData.role === 'coach' && 'Can create programs and manage assigned clients'}
              {formData.role === 'super_admin' && 'Full system access including user management'}
            </p>
          </div>

          {/* Coach Assignment (only for client role, but hidden since we're only creating coach/admin) */}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: 'transparent',
                border: `2px solid ${theme.textMuted}`,
                color: theme.textMuted,
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px 24px',
                backgroundColor: theme.gold,
                border: 'none',
                color: theme.surface1,
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                opacity: loading ? 0.5 : 1
              }}
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </>
      )}
      </div>
    </div>
  );
};

export default CreateUserModal;
